import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import type { FlashCardUI } from "../models/FlashcardUI";
import styles from "./FlashcardFooterWeb.module.scss";

type FlashcardFooterWebProps = {
  card: FlashCardUI;
  onBookmark: () => void;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalCards: number;
  onLike: () => void;
  onDislike: () => void;
  questionIds: string[];
  isNarrowLayout?: boolean | undefined;
};

export const FlashcardFooterWeb = memo(function FlashcardFooterWeb({
  card,
  onBookmark,
  onNext,
  onPrevious,
  currentIndex,
  totalCards,
  onLike,
  onDislike,
  questionIds,
  isNarrowLayout,
}: FlashcardFooterWebProps) {
  const { t } = useTranslation("selflearn");
  const { theme } = useTheme();
  const extra = questionIds.length ? 1 : 0;
  const denom = totalCards + extra;
  const previousArrowColor =
    currentIndex === 0 ? theme.colors.neutral[60] : theme.colors.primary[40];
  const nextArrowColor =
    currentIndex === denom - 1
      ? theme.colors.neutral[60]
      : theme.colors.primary[40];

  const bookmarkIconButton = useCallback(() => {
    return (
      <button
        type="button"
        className={styles.iconBtn}
        onClick={onBookmark}
        aria-label={t("flashcards.a11y.bookmark")}
      >
        <Icon
          name={card.isBookmarkSelected ? "bookmarkSl" : "unBookmarkSl"}
          size={24}
          color={theme.colors.primary[40]}
        />
      </button>
    );
  }, [isNarrowLayout, card.isBookmarkSelected, onBookmark, t]);

  return (
    <footer className={styles.footer}>
      <div className={styles.group}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onLike}
          aria-label={t("flashcards.a11y.like")}
        >
          <Icon
            name={card.isLiked ? "likeActiveSl" : "likeInActiveSl"}
            size={24}
            color={theme.colors.primary[40]}
          />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onDislike}
          aria-label={t("flashcards.a11y.dislike")}
        >
          <Icon
            name={card.isDisLiked ? "disLikeActiveSl" : "disLikeInActiveSl"}
            size={24}
            color={theme.colors.primary[40]}
          />
        </button>
      </div>
      {isNarrowLayout ? bookmarkIconButton() : null}
      <div className={styles.group}>
        {isNarrowLayout ? null : bookmarkIconButton()}
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onPrevious}
          aria-label={t("flashcards.a11y.previousCard")}
        >
          <Icon name="previousArrowSl" size={24} color={previousArrowColor} />
        </button>
        {isNarrowLayout ? (
          <p className={styles.progressText}>
            {currentIndex + 1}/{denom}
          </p>
        ) : null}
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onNext}
          aria-label={t("flashcards.a11y.nextCard")}
        >
          <Icon name="nextArrowSl" size={24} color={nextArrowColor} />
        </button>
      </div>
    </footer>
  );
});
